// views/PaymentSuccess/index.jsx - COMPLETE UPDATED VERSION
import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useUTM } from 'context/UTMContext';
import { useModal } from 'context';
import { useConsent } from 'context/ConsentContext';
import { Login } from 'modals';
import { selectAuthToken } from 'store/auth/selectors';
import { fetchTransactionDetails } from 'services/transaction.service';
import { gtm } from 'GTM';
import { formatPhoneE164 } from 'helpers/tracking';
import { formatPhoneWithCountry } from 'helpers/tracking';

import styles from './styles.module.scss';

// Module-level variables
let globalEventDispatched = false;
let globalTransactionProcessed = false;

const toNumber = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return value;
  const cleaned = String(value).replace(/\s/g, '').replace(',', '.').replace(/[^\d.-]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

// ⭐ Handle the actual transaction data structure
const collectConversionData = (transactionData, orderRef) => {
  console.log('[PaymentSuccess] 📦 Raw transaction data:', transactionData);
  
  // Extract items from the transaction data
  let items = [];
  
  // Check if we have item data in the response
  if (transactionData.id && transactionData.name) {
    // Single item - format as array
    items = [{
      id: transactionData.id || 'unknown',
      name: transactionData.name || 'Unknown Product',
      price: toNumber(transactionData.price || transactionData.value || 0),
      quantity: 1, // Default to 1 since we don't have quantity
      category: transactionData.category || '',
      brand: transactionData.brand || ''
    }];
    console.log('[PaymentSuccess] 📦 Single item extracted:', items[0]);
  } else if (Array.isArray(transactionData.items) && transactionData.items.length > 0) {
    // Multiple items (if API ever returns array)
    items = transactionData.items.map(item => ({
      id: item.item_id || item.id || 'unknown',
      name: item.item_name || item.name || 'Unknown Product',
      price: toNumber(item.price || item.value || 0),
      quantity: item.quantity || 1,
      category: item.item_category || item.category || '',
      brand: item.item_brand || item.brand || ''
    }));
    console.log('[PaymentSuccess] 📦 Multiple items extracted:', items.length);
  } else {
    console.log('[PaymentSuccess] ⚠️ No items found in transaction data');
  }
  
  return {
    transaction_id: transactionData.transaction_id || orderRef || `TXN_${Date.now()}`,
    value: toNumber(transactionData.value || transactionData.basket_total || 0),
    tax: toNumber(transactionData.tax || 0),
    shipping: toNumber(transactionData.shipping || 0),
    currency: transactionData.currency || 'EUR',
    coupon: transactionData.coupon || undefined,
    items: items,
  };
};

// Collect visitor data
const collectVisitorData = (utmParams, hasConsent) => {
  if (!hasConsent('analytics')) {
    return null;
  }
  
  const visitorData = {};
  
  // Only add if value exists and is not empty
  if (utmParams.utm_source) visitorData.utm_source = utmParams.utm_source;
  if (utmParams.utm_medium) visitorData.utm_medium = utmParams.utm_medium;
  if (utmParams.utm_campaign) visitorData.utm_campaign = utmParams.utm_campaign;
  if (utmParams.utm_content) visitorData.utm_content = utmParams.utm_content;
  if (utmParams.utm_term) visitorData.utm_term = utmParams.utm_term;
  if (utmParams.utm_id) visitorData.utm_id = utmParams.utm_id;
  if (utmParams.gclid) visitorData.gclid = utmParams.gclid;
  
  return Object.keys(visitorData).length > 0 ? visitorData : null;
};

// ⭐ SIMPLIFIED: Extract payment status using the same logic as UI
const extractPaymentStatus = (response) => {
  console.log('[PaymentSuccess] 🔍 Extracting payment status');
  
  // If no response
  if (!response) {
    return 'unknown';
  }
  
  // ⭐ CRITICAL: Check for warning FIRST (same as UI logic)
  if (response.warning && Array.isArray(response.warning) && response.warning.length > 0) {
    console.log('[PaymentSuccess] ❌ Warning exists - payment failed');
    return 'failed';
  }
  
  // ⭐ Check for success (same as UI logic: response?.message && response?.data)
  if (response.message && response.data) {
    console.log('[PaymentSuccess] ✅ Message and data exist - payment successful');
    return 'success';
  }
  
  // If we have data but no message (edge case)
  if (response.data) {
    console.log('[PaymentSuccess] 📦 Data exists but no message - checking transaction');
    // If we have transaction data, it's likely a success
    if (response.data.transaction_id || response.data.id) {
      return 'success';
    }
    return 'completed';
  }
  
  // Check for success flag as fallback
  if (response.success === true) {
    return 'success';
  }
  
  if (response.success === false) {
    return 'failed';
  }
  
  // Default
  console.log('[PaymentSuccess] ⚠️ No clear status, defaulting to unknown');
  return 'unknown';
};

const PaymentSuccess = () => {
  const { t } = useTranslation('global');
  const [searchParams] = useSearchParams();
  
  const { utmParams } = useUTM();
  const { openModal } = useModal();
  const { hasConsent } = useConsent();
  
  const paylinetoken = searchParams.get('paylinetoken');
  const orderRef = searchParams.get('orderRef');
  const accessToken = useSelector(selectAuthToken);
  const currentUser = useSelector((state) => state.auth.currentUser);
  
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  
  const componentMountedRef = useRef(true);
  const authCheckedRef = useRef(false);
  const modalOpenedRef = useRef(false);
  const apiCalledRef = useRef(false);
  const transactionStartedRef = useRef(false);
  const eventDispatchedRef = useRef(false);
  const userCheckTimeoutRef = useRef(null);

  // Build user data for enhanced conversions
  const buildUserData = useCallback(() => {
    console.log('[PaymentSuccess] 🔍 Building user data from:', currentUser);
    
    const userData = {};
    
    // Email
    if (currentUser?.email) {
      userData.email = currentUser.email;
    }
    
    // Phone - try multiple fields
    const phoneNumber = currentUser?.phone || currentUser?.contact_phone || currentUser?.second_contact_phone;
    if (phoneNumber) {
      userData.phone_number = formatPhoneWithCountry(phoneNumber, currentUser);
    }
    
    // Address
    const address = {};
    const companyAddress = currentUser?.company_address;
    
    if (currentUser?.first_name) address.first_name = currentUser.first_name;
    if (currentUser?.last_name) address.last_name = currentUser.last_name;
    if (companyAddress?.address) address.street = companyAddress.address;
    if (companyAddress?.city) address.city = companyAddress.city;
    if (companyAddress?.postal_code) address.postal_code = companyAddress.postal_code;
    if (companyAddress?.district) address.region = companyAddress.district;
    
    // Country fallback
    if (companyAddress?.country && companyAddress.country !== '') {
      address.country = companyAddress.country;
    } else if (currentUser?.country_of_residence) {
      address.country = currentUser.country_of_residence;
    } else if (currentUser?.nationality) {
      address.country = currentUser.nationality;
    } else {
      address.country = "FR";
    }
    
    // Clean address
    Object.keys(address).forEach(key => {
      if (!address[key] || address[key] === '') {
        delete address[key];
      }
    });
    
    if (Object.keys(address).length > 0) {
      userData.address = address;
    }
    
    // Clean user data
    Object.keys(userData).forEach(key => {
      if (userData[key] === undefined || userData[key] === null || userData[key] === '') {
        delete userData[key];
      }
    });
    
    console.log('[PaymentSuccess] ✅ Built user data:', userData);
    return userData;
  }, [currentUser]);

  // ⭐ Track purchase event with payment_status
  const dispatchPurchaseEvent = useCallback((transactionData, paymentStatus, rawResponse) => {
    // Prevent duplicate events
    if (eventDispatchedRef.current || globalEventDispatched) {
      console.log('[PaymentSuccess] ⏸️ Purchase event already dispatched, skipping');
      return;
    }
    
    console.log('[PaymentSuccess] 🚀 Dispatching purchase event');
    console.log('[PaymentSuccess] 💳 Payment status:', paymentStatus);
    
    const conversionData = collectConversionData(transactionData, orderRef);
    const visitorData = collectVisitorData(utmParams, hasConsent);
    const userDataForEnhanced = buildUserData();
    
    console.log('[PaymentSuccess] 📊 Conversion data:', {
      transaction_id: conversionData.transaction_id,
      value: conversionData.value,
      items: conversionData.items.length,
      payment_status: paymentStatus
    });
    console.log('[PaymentSuccess] 📊 User data for enhanced conversions:', userDataForEnhanced);
    
    // Mark as dispatched
    eventDispatchedRef.current = true;
    globalEventDispatched = true;
    
    const purchaseData = {
      transaction_id: conversionData.transaction_id,
      value: conversionData.value,
      tax: conversionData.tax,
      shipping: conversionData.shipping,
      currency: conversionData.currency,
      coupon: conversionData.coupon,
      items: conversionData.items,
      payment_status: paymentStatus, // ⭐ Dynamic payment_status
      data_limited: !hasConsent('analytics'),
      ...(visitorData && {
        utm_source: visitorData.utm_source,
        utm_medium: visitorData.utm_medium,
        utm_campaign: visitorData.utm_campaign,
        utm_content: visitorData.utm_content,
        utm_term: visitorData.utm_term,
        utm_id: visitorData.utm_id,
        gclid: visitorData.gclid
      }),
      user_data: userDataForEnhanced
    };
    
    console.log('[PaymentSuccess] 📤 Sending to GTM with payment_status:', paymentStatus);
    gtm.trackPurchase(purchaseData);
    
    // Facebook Pixel (marketing consent) - Only for successful payments
    if (paymentStatus === 'success' && hasConsent('marketing')) {
      if (window.fbq) {
        window.fbq('track', 'Purchase', {
          value: conversionData.value,
          currency: conversionData.currency,
          content_ids: conversionData.items.map(item => item.id),
          content_type: 'product'
        });
      }
      if (window.lintrk) {
        window.lintrk('track', { conversion_id: conversionData.transaction_id });
      }
    }
    
  }, [orderRef, utmParams, hasConsent, buildUserData]);

  // Check authentication
  useEffect(() => {
    if (!accessToken && !authCheckedRef.current && !modalOpenedRef.current) {
      authCheckedRef.current = true;
      modalOpenedRef.current = true;
      
      console.log('[PaymentSuccess] 🔓 Opening login modal');
      
      if (paylinetoken && orderRef) {
        sessionStorage.setItem('pendingPaymentToken', paylinetoken);
        sessionStorage.setItem('pendingOrderRef', orderRef);
      }

      openModal({
        content: <Login />,
        title: "Login",
        onClose: () => {
          console.log('[PaymentSuccess] Login modal closed');
          modalOpenedRef.current = false;
        }
      });
    }
  }, [accessToken, openModal, paylinetoken, orderRef]);

  // ⭐ MAIN EFFECT: Load transaction
  useEffect(() => {
    // Don't proceed if no access token
    if (!accessToken) {
      console.log('[PaymentSuccess] ⏸️ No access token, waiting for login');
      return;
    }

    // ⭐ CRITICAL: Wait for user data before proceeding
    if (!currentUser) {
      console.log('[PaymentSuccess] ⏳ Waiting for user data to load...');
      // Clear any existing timeout
      if (userCheckTimeoutRef.current) {
        clearTimeout(userCheckTimeoutRef.current);
      }
      // Check again after 500ms
      userCheckTimeoutRef.current = setTimeout(() => {
        console.log('[PaymentSuccess] 🔄 Retrying user data check...');
        setLoading(true);
      }, 500);
      return;
    }

    // Clear timeout if user data is now available
    if (userCheckTimeoutRef.current) {
      clearTimeout(userCheckTimeoutRef.current);
      userCheckTimeoutRef.current = null;
    }

    // Prevent duplicate processing GLOBALLY
    if (globalTransactionProcessed) {
      console.log('[PaymentSuccess] Transaction already processed globally, skipping');
      return;
    }

    // Prevent duplicate API calls locally
    if (apiCalledRef.current || transactionStartedRef.current) {
      console.log('[PaymentSuccess] Transaction already started or API called, skipping');
      return;
    }

    // Mark as processed GLOBALLY immediately
    globalTransactionProcessed = true;
    transactionStartedRef.current = true;
    componentMountedRef.current = true;
    
    console.log('[PaymentSuccess] ✅ User data ready, starting transaction load');
    console.log('[PaymentSuccess] 👤 User:', {
      email: currentUser?.email,
      first_name: currentUser?.first_name,
      last_name: currentUser?.last_name,
    });
    
    const loadTransaction = async () => {
      apiCalledRef.current = true;

      if (!paylinetoken || !orderRef) {
        console.log('[PaymentSuccess] ❌ Missing paylinetoken or orderRef');
        if (componentMountedRef.current) {
          setLoading(false);
          setWarnings([{ message: t('booking.paymentStatus.invalidPaymentDetails') }]);
        }
        // ⭐ Dispatch with 'missing_parameters' status
        const paymentStatus = 'missing_parameters';
        const fallbackData = {
          transaction_id: orderRef || `TXN_${Date.now()}`,
          value: 0,
          tax: 0,
          shipping: 0,
          currency: 'EUR',
          items: []
        };
        dispatchPurchaseEvent(fallbackData, paymentStatus, { error: 'Missing paylinetoken or orderRef' });
        return;
      }

      try {
        console.log('[PaymentSuccess] 📡 Calling fetchTransactionDetails');
        const response = await fetchTransactionDetails(
          orderRef,
          paylinetoken,
          accessToken
        );

        console.log('[PaymentSuccess] 📡 Response received:', response);
        
        if (componentMountedRef.current) {
          setLoading(false);
        }

        // ⭐ Determine payment status using the SAME logic as UI
        let paymentStatus = 'unknown';
        
        // Check warnings first (same as UI)
        if (response?.warning && Array.isArray(response.warning) && response.warning.length > 0) {
          paymentStatus = 'failed';
          console.log('[PaymentSuccess] ❌ Payment failed - warnings detected');
          
          if (componentMountedRef.current) {
            setWarnings(response.warning);
          }
          
          // Still dispatch with failed status
          const fallbackData = response.data || {
            transaction_id: orderRef || `TXN_${Date.now()}`,
            value: 0,
            tax: 0,
            shipping: 0,
            currency: 'EUR',
            items: []
          };
          dispatchPurchaseEvent(fallbackData, paymentStatus, response);
          return;
        }

        // Check success (same as UI: response?.message && response?.data)
        if (response?.message && response?.data) {
          paymentStatus = 'success';
          console.log('[PaymentSuccess] ✅ Payment successful');
          
          if (componentMountedRef.current) {
            setMessage(response.message);
          }
          
          // ⭐ Dispatch with success status
          dispatchPurchaseEvent(response.data, paymentStatus, response);
          
          sessionStorage.removeItem('pendingPaymentToken');
          sessionStorage.removeItem('pendingOrderRef');
          return;
        }

        // No data (same as UI)
        console.log('[PaymentSuccess] ❌ No data in response');
        if (componentMountedRef.current) {
          setErrors([t('booking.paymentStatus.failedTransactionDetails')]);
        }
        
        const fallbackData = {
          transaction_id: orderRef || `TXN_${Date.now()}`,
          value: 0,
          tax: 0,
          shipping: 0,
          currency: 'EUR',
          items: []
        };
        dispatchPurchaseEvent(fallbackData, 'no_data', response);
        
      } catch (err) {
        console.error('[PaymentSuccess] ❌ Error:', err);
        if (componentMountedRef.current) {
          setLoading(false);
          setErrors([t('booking.paymentStatus.unexpectedError')]);
        }
        
        const fallbackData = {
          transaction_id: orderRef || `TXN_${Date.now()}`,
          value: 0,
          tax: 0,
          shipping: 0,
          currency: 'EUR',
          items: []
        };
        dispatchPurchaseEvent(fallbackData, 'exception', { error: err.message });
      }
    };

    loadTransaction();

    return () => {
      console.log('[PaymentSuccess] 🧹 Cleanup');
      componentMountedRef.current = false;
      if (userCheckTimeoutRef.current) {
        clearTimeout(userCheckTimeoutRef.current);
        userCheckTimeoutRef.current = null;
      }
    };
  }, [accessToken, currentUser, paylinetoken, orderRef, t, dispatchPurchaseEvent]);

  return (
    <div className={styles.paymentSuccess}>
      <div className={styles.container}>
        {loading && (
          <div className={styles.loader}>
            <div className={styles.cwLoader} />
            <div className={styles.loadingText}>
              {!currentUser && accessToken 
                ? 'Loading user data...' 
                : t('booking.paymentStatus.loading')}
            </div>
          </div>
        )}

        {!loading && errors.length > 0 && (
          <div className={styles.alertError}>
            <div className={styles.alertContent}>
              <div className={styles.alertIcon}>⚠️</div>
              <div>
                <h3>{t('booking.paymentStatus.errorTitle')}</h3>
                {errors.map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!loading && warnings.length > 0 && (
          <div className={styles.alertWarning}>
            <div className={styles.alertContent}>
              <div className={styles.alertIcon}>ℹ️</div>
              <div>
                <h3>{t('booking.paymentStatus.noteTitle')}</h3>
                {warnings.map((w, i) => (
                  <div key={i}>
                    {w.spaceName
                      ? t('booking.paymentStatus.invalidDateForSpace', { spaceName: w.spaceName })
                      : w.message}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!loading && message && (
          <div className={styles.successMessage}>
            <div className={styles.successContent}>
              <div className={styles.successIcon}>✓</div>
              <h2>{t('booking.paymentStatus.paymentSuccessful')}</h2>
              <p>{t('booking.paymentStatus.bookingConfirmed')}</p>
              <p>
                <strong>{t('booking.paymentStatus.order')}:</strong> {orderRef}
              </p>
              
              <div className={styles.successActions}>
                <a
                  className={styles.primaryButton}
                  href={"/dashboard/bookings"}
                >
                  {t('booking.step4.viewBookings')}
                </a>
                <a
                  className={styles.secondaryButton}
                  href={"/"}
                >
                  {t('booking.paymentStatus.goHome')}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;